// src/hooks/useOrderSSE.ts

import { useEffect, useCallback, useState } from 'react';
import { soundService } from '../services/soundService';
import { OrderStatus, OrderType } from '../types';
import { sseClient } from '../api/sseClient';

interface OrderEvent {
  type: 'newOrder' | 'orderUpdated' | 'orderCancelled' | 'connected';
  restaurantId?: string;
  order?: any;
  timestamp?: string;
  message?: string;
  cancelledBy?: 'customer' | 'restaurant'; // من قام بإلغاء الطلب
}

interface UseOrderSSEOptions {
  onNewOrder?: (order: any) => void;
  onOrderUpdated?: (order: any) => void;
  onOrderCancelled?: (order: any) => void;
  onError?: (error: Error) => void;
  enabled?: boolean;
}

/**
 * Hook للاتصال بـ SSE باستخدام SSEClient
 */
export const useOrderSSE = (options: UseOrderSSEOptions = {}) => {
  const {
    onNewOrder,
    onOrderUpdated,
    onOrderCancelled,
    onError,
    enabled = true,
  } = options;

  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string>('');

  /**
   * معالجة الأحداث الواردة
   */
  const handleMessage = useCallback(
    (data: OrderEvent) => {
      try {
        // رسالة الاتصال الأولية
        if (data.type === 'connected') {
          console.log('✅ SSE Connected:', data.message);
          setConnected(true);
          setError('');
          return;
        }

        // التحقق من وجود order في الـ event
        if (!data.order) {
          console.warn('⚠️ Received event without order data');
          return;
        }

        // أحداث الطلبات
        switch (data.type) {
          case 'newOrder':
            console.log('🆕 New Order:', data.order.orderNumber);
            soundService.playNewOrderSound();
            onNewOrder?.(data.order);
            break;

          case 'orderUpdated':
            console.log('🔄 Order Updated:', data.order.orderNumber);
            if (data.order.status === OrderStatus.COMPLETED && data.order.orderType !== OrderType.CASHIER) {
              soundService.playOrderUpdatedSound();
            }
            onOrderUpdated?.(data.order);
            break;

          case 'orderCancelled':
            console.log('❌ Order Cancelled:', data.order.orderNumber, '(by', data.cancelledBy || 'unknown', ')');
            // Only play sound if cancelled by customer (not by restaurant owner)
            if (data.cancelledBy === 'customer') {
              soundService.playOrderCancelledSound();
            }
            onOrderCancelled?.(data.order);
            break;

          default:
            console.log('📨 Unknown event type:', data.type);
        }
      } catch (err) {
        console.error('Error handling SSE message:', err);
      }
    },
    [onNewOrder, onOrderUpdated, onOrderCancelled]
  );

  /**
   * معالجة الأخطاء
   */
  const handleError = useCallback(
    (err: Error) => {
      console.error('❌ SSE Error:', err);
      setConnected(false);
      setError('فشل الاتصال بخادم الإشعارات');
      onError?.(err);
    },
    [onError]
  );

  /**
   * عند فتح الاتصال
   */
  const handleOpen = useCallback(() => {
    console.log('📡 SSE Connection opened');
    setConnected(true);
    setError('');
  }, []);

  /**
   * عند إغلاق الاتصال
   */
  const handleClose = useCallback(() => {
    console.log('🔌 SSE Connection closed');
    setConnected(false);
  }, []);

  /**
   * إعادة الاتصال يدوياً
   */
  const reconnect = useCallback(() => {
    console.log('🔄 Manual reconnect triggered');
    sseClient.resetReconnectAttempts();
    setError('');
    
    sseClient.connect({
      url: '/orders/restaurant/stream',
      onMessage: handleMessage,
      onError: handleError,
      onOpen: handleOpen,
      onClose: handleClose,
    });
  }, [handleMessage, handleError, handleOpen, handleClose]);

  /**
   * قطع الاتصال يدوياً
   */
  const disconnect = useCallback(() => {
    sseClient.disconnect();
    setConnected(false);
  }, []);

  /**
   * Effect للاتصال التلقائي
   */
  useEffect(() => {
    if (!enabled) {
      sseClient.disconnect();
      return;
    }

    // الاتصال بـ SSE
    sseClient.connect({
      url: '/orders/restaurant/stream',
      onMessage: handleMessage,
      onError: handleError,
      onOpen: handleOpen,
      onClose: handleClose,
    });

    // التنظيف عند unmount
    return () => {
      sseClient.disconnect();
    };
  }, [enabled, handleMessage, handleError, handleOpen, handleClose]);

  return {
    connected,
    error,
    reconnect,
    disconnect,
  };
};

export default useOrderSSE;